import React, { useState, useMemo, useCallback } from 'react';
import logger from '../../utils/logger';
import { useMasterData } from '../../hooks/useMasterData';
import { useAuth } from '../../hooks/useAuth';
import {
  ClientCompat,
  BusinessPartner,
  BusinessPartnerDataSource,
  BusinessPartnerCategory,
  BusinessPartnerType,
  BusinessPartnerRole,
  businessPartnerToClientCompat,
  Role,
} from '../../types';
import {
  PlusCircleIcon,
  ListBulletIcon,
  BuildingOffice2Icon,
  FunnelIcon,
  TagIcon,
  EyeIcon,
  UserIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, PaginatedCards, PaginatedTable, PageTitle } from '../../components/ui';
import { BusinessPartnerDetailModal } from '../../components/business-partner/BusinessPartnerDetailModal';
import { BusinessPartnerFormModal } from '../../components/business-partner/BusinessPartnerFormModal';

const getCategoryLabel = (category: BusinessPartnerCategory) => {
  switch (category) {
    case BusinessPartnerCategory.DEALERSHIP:
      return '売買・買取店';
    case BusinessPartnerCategory.AUCTION:
      return 'オークション会場';
    case BusinessPartnerCategory.OTHER:
      return 'その他';
    default:
      return '不明';
  }
};

const getRoleLabel = (role: BusinessPartnerRole): string => {
  const labels = {
    [BusinessPartnerRole.BILLING]: '請求先',
    [BusinessPartnerRole.PICKUP]: '引き取り先',
    [BusinessPartnerRole.PICKUP_LOCATION]: '引取拠点',
    [BusinessPartnerRole.DELIVERY]: '搬入先',
    [BusinessPartnerRole.DELIVERY_LOCATION]: '搬入拠点',
    [BusinessPartnerRole.BOTH]: '引取・搬入両方',
  };
  return labels[role] || role;
};

export const PartnerListPage: React.FC = () => {
  const { user } = useAuth();
  const {
    businessPartners,
    companies: _companies,
    loading,
    addClient: _addClient,
    updateClient: _updateClient,
    addBusinessPartner,
    updateBusinessPartner,
    getCompanyPartnerSelectionsByCompanyId,
    refreshData,
    cases,
    getDeadlineRulesByBusinessPartnerId,
  } = useMasterData();
  const [_isFormVisible, _setIsFormVisible] = useState(false);
  const [_editingPartner, _setEditingPartner] = useState<BusinessPartner | null>(null);
  const [_isEditFormVisible, _setIsEditFormVisible] = useState(false);
  const [_editingClient, _setEditingClient] = useState<ClientCompat | null>(null);
  const [_createError, _setCreateError] = useState<string | null>(null);

  // フィルタ状態
  const [filterCategory, setFilterCategory] = useState<BusinessPartnerCategory | 'all'>('all');

  // 詳細モーダル状態
  const [_detailModalOpen, _setDetailModalOpen] = useState(false);
  const [_selectedPartner, _setSelectedPartner] = useState<(typeof allPartners)[0] | null>(null);
  const [_selectedBusinessPartner, _setSelectedBusinessPartner] = useState<BusinessPartner | null>(
    null
  );

  // 認証ユーザーの会社IDとロールを使用
  const companyId = user?.companyId || 'e32a0499-6456-44ac-9b16-b0001098c259';
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

  // 締切ルールの有無を判定する関数
  const hasDeadlineRules = useCallback(
    (partnerId: string) => {
      const partnerRules = getDeadlineRulesByBusinessPartnerId(partnerId, companyId);
      return partnerRules.length > 0;
    },
    [getDeadlineRulesByBusinessPartnerId, companyId]
  );

  // 案件で使用されている取引先IDを収集
  const usedPartnerIds = useMemo(() => {
    const ids = new Set<string>();
    cases.forEach(case_ => {
      if (
        case_.status === 'provisional' ||
        case_.status === 'confirmed' ||
        case_.status === 'assigned' ||
        case_.status === 'picked_up' ||
        case_.status === 'in_transit' ||
        case_.status === 'delivered' ||
        case_.status === 'reported'
      ) {
        if (case_.billingPartnerId) ids.add(case_.billingPartnerId);
        if (case_.pickupPartnerId) ids.add(case_.pickupPartnerId);
        if (case_.deliveryPartnerId) ids.add(case_.deliveryPartnerId);
      }
    });
    logger.info('[PartnerListPage] 使用中の取引先ID:', Array.from(ids));
    return ids;
  }, [cases]);

  // BusinessPartnersのみを使用した統合リスト作成（CompanyPartnerSelectionベース）
  const allPartners = useMemo(() => {
    // SUPER_ADMINの場合は全取引先を表示（フィルタリングなし）
    if (isSuperAdmin) {
      logger.info('[PartnerListPage] SUPER_ADMIN検出 - 全取引先を表示');
      const businessPartnersList = businessPartners.map(bp => ({
        ...businessPartnerToClientCompat(bp),
        source: 'businessPartner' as const,
        displayName: bp.displayName,
        originalType: bp.type,
        dataSource: bp.dataSource,
        category: bp.category,
        availableRoles: bp.availableRoles, // 対応可能役割を追加
      }));

      // カテゴリフィルタのみ適用
      let filtered = businessPartnersList;
      if (filterCategory !== 'all') {
        filtered = filtered.filter(partner => partner.category === filterCategory);
      }

      return filtered.sort((a, b) => {
        // データソース順（独自追加を優先）
        const aIsCustom = a.dataSource === BusinessPartnerDataSource.CUSTOM;
        const bIsCustom = b.dataSource === BusinessPartnerDataSource.CUSTOM;
        if (aIsCustom && !bIsCustom) return -1;
        if (!aIsCustom && bIsCustom) return 1;
        // 同じデータソース内では会社名順
        return a.companyName.localeCompare(b.companyName);
      });
    }

    // 通常ユーザーの場合は既存のフィルタリング処理
    // 会社の取引先選択設定を取得
    const companyPartnerSelections = getCompanyPartnerSelectionsByCompanyId(companyId);

    logger.info('[PartnerListPage] 設定確認:', {
      userCompanyId: user?.companyId,
      finalCompanyId: companyId,
      companyPartnerSelections: companyPartnerSelections.length,
      businessPartnersCount: businessPartners.length,
    });

    const businessPartnersList = businessPartners
      .filter(partner => {
        // CompanyPartnerSelection設定がない場合でも、使用中なら表示
        if (companyPartnerSelections.length === 0) {
          // 案件で使用中の取引先は表示する
          if (usedPartnerIds.has(partner.id)) {
            logger.info(`[PartnerListPage] ${partner.name} - 案件で使用中のため表示`);
            return true;
          }
          logger.info(
            `[PartnerListPage] CompanyPartnerSelection設定なし - ${partner.name}を非表示`
          );
          return false;
        }

        // CompanyPartnerSelection設定がある場合のみ、その設定に従う
        const partnerSelection = companyPartnerSelections.find(
          selection => selection.businessPartnerId === partner.id
        );

        // 該当パートナーの設定がない場合でも、使用中なら表示
        if (!partnerSelection) {
          if (usedPartnerIds.has(partner.id)) {
            logger.info(
              `[PartnerListPage] ${partner.name} - 選択されていないが案件で使用中のため表示`
            );
            return true;
          }
          logger.info(
            `[PartnerListPage] ${partner.name} - CompanyPartnerSelection設定なしのため除外`
          );
          return false;
        }

        // 設定が無効化されている場合は除外
        if (!partnerSelection.isEnabled) {
          logger.info(`[PartnerListPage] ${partner.name} - 無効化されているため除外`);
          return false;
        }

        logger.info(`[PartnerListPage] ${partner.name} - 表示許可`, {
          dataSource: partner.dataSource,
          isEnabled: partnerSelection.isEnabled,
        });
        return true;
      })
      .map(bp => ({
        ...businessPartnerToClientCompat(bp),
        source: 'businessPartner' as const,
        displayName: bp.displayName,
        originalType: bp.type,
        dataSource: bp.dataSource,
        category: bp.category,
        availableRoles: bp.availableRoles, // 対応可能役割を追加
      }));

    let filtered = businessPartnersList;

    // カテゴリフィルタ
    if (filterCategory !== 'all') {
      filtered = filtered.filter(partner => partner.category === filterCategory);
    }

    const finalResult = filtered.sort((a, b) => {
      // 1. データソース順（独自追加を優先）
      const aIsCustom = a.dataSource === BusinessPartnerDataSource.CUSTOM;
      const bIsCustom = b.dataSource === BusinessPartnerDataSource.CUSTOM;

      if (aIsCustom && !bIsCustom) return -1;
      if (!aIsCustom && bIsCustom) return 1;

      // 2. 同じデータソース内では会社名順
      return a.companyName.localeCompare(b.companyName);
    });

    // フィルタリング結果をログ出力
    logger.info('[PartnerListPage] フィルタリング結果:', {
      originalBusinessPartners: businessPartners.length,
      filteredBusinessPartners: businessPartnersList.length,
      totalPartners: finalResult.length,
      rakuhaiFiltered: businessPartnersList.filter(
        bp => bp.dataSource === BusinessPartnerDataSource.RAKUHAI_PROVIDED
      ).length,
      customFiltered: businessPartnersList.filter(
        bp => bp.dataSource === BusinessPartnerDataSource.CUSTOM
      ).length,
    });

    return finalResult;
  }, [
    businessPartners,
    filterCategory,
    companyId,
    getCompanyPartnerSelectionsByCompanyId,
    isSuperAdmin,
    usedPartnerIds,
  ]);

  const initialFormState = useMemo(
    () => ({
      companyName: '',
      address: '',
      contactPerson: '',
      email: '',
      phone: '',
      category: BusinessPartnerCategory.DEALERSHIP,
      type: BusinessPartnerType.CORPORATE, // デフォルトは法人
      availableRoles: [
        BusinessPartnerRole.BILLING,
        BusinessPartnerRole.PICKUP,
        BusinessPartnerRole.DELIVERY,
      ] as BusinessPartnerRole[], // デフォルトは全役割
      notes: '', // デフォルトは空文字
    }),
    []
  );
  const [_newClientData, _setNewClientData] = useState(initialFormState);

  // TODO: 将来的にフォーム機能を実装予定
  // const _handleInputChange = useCallback(
  //   (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
  //     const { name, value } = e.target;
  //     setNewClientData(prev => ({ ...prev, [name]: value }));
  //   },
  //   []
  // );

  // const _handleRoleChange = useCallback((role: BusinessPartnerRole, checked: boolean) => {
  //   setNewClientData(prev => ({
  //     ...prev,
  //     availableRoles: checked
  //       ? [...prev.availableRoles, role]
  //       : prev.availableRoles.filter(r => r !== role),
  //   }));
  // }, []);

  // TODO: 将来実装予定
  // const _handleSubmit = useCallback(
  //   async (e: React.FormEvent) => {
  //     e.preventDefault();
  //     if (
  //       !newClientData.companyName ||
  //       !newClientData.address ||
  //       !newClientData.email ||
  //       !newClientData.phone
  //     ) {
  //       alert('必須項目（会社名、住所、メールアドレス、電話番号）を入力してください。');
  //       return;
  //     }

  //     if (newClientData.availableRoles.length === 0) {
  //       alert('対応可能役割を最低1つ選択してください。');
  //       return;
  //     }

  //     try {
  //       if (editingClient) {
  //         // 編集モード
  //         await updateClient(editingClient.id, {
  //           ...newClientData,
  //           status: editingClient.status, // ステータスは保持
  //         });
  //       } else {
  //         // 新規追加モード
  //         await addClient({
  //           id: crypto.randomUUID(), // ID生成を追加
  //           ...newClientData,
  //           status: 'Active', // 新規は有効に設定
  //         });
  //       }

  //       // フォームリセット
  //       _setIsFormVisible(false);
  //       setEditingClient(null);
  //       setNewClientData(initialFormState);
  //     } catch (error) {
  //       logger.error('取引先の保存に失敗しました:', error);
  //       alert('保存に失敗しました。もう一度お試しください。');
  //     }
  //   },
  //   [newClientData, editingClient, updateClient, addClient, initialFormState]
  // );

  // TODO: 将来実装予定
  // const _cancelEditing = useCallback(() => {
  //   setEditingClient(null);
  //   setNewClientData(initialFormState);
  //   _setIsFormVisible(false);
  // }, [initialFormState]);

  // 詳細表示処理
  const handleShowDetail = useCallback(
    (partner: (typeof allPartners)[0]) => {
      _setSelectedPartner(partner);

      // BusinessPartner型の詳細情報を取得
      if (partner.source === 'businessPartner') {
        const businessPartnerDetail = businessPartners.find(bp => bp.id === partner.id);
        _setSelectedBusinessPartner(businessPartnerDetail || null);
      } else {
        _setSelectedBusinessPartner(null);
      }

      _setDetailModalOpen(true);
    },
    [businessPartners]
  );

  // 詳細モーダルを閉じる
  const _handleCloseDetailModal = useCallback(() => {
    _setDetailModalOpen(false);
    _setSelectedPartner(null);
    _setSelectedBusinessPartner(null);
  }, []);

  const FilterSection = () => (
    <div className='bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md mb-6'>
      <div className='flex items-center mb-3'>
        <FunnelIcon className='h-5 w-5 text-gray-500 mr-2' />
        <h3 className='text-sm font-medium text-gray-700 dark:text-gray-300'>フィルタ</h3>
      </div>
      <div className='grid grid-cols-1 gap-4'>
        <div>
          <label className='block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'>
            カテゴリ
          </label>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value as BusinessPartnerCategory | 'all')}
            className='w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500'
          >
            <option value='all'>すべて</option>
            <option value={BusinessPartnerCategory.DEALERSHIP}>売買・買取店</option>
            <option value={BusinessPartnerCategory.AUCTION}>オークション会場</option>
            <option value={BusinessPartnerCategory.OTHER}>その他</option>
          </select>
        </div>
      </div>
    </div>
  );

  const _PartnerList = () => (
    <div>
      <PageTitle icon={ListBulletIcon} title={`取引先一覧 (${allPartners.length})`} />
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0'>
        <div></div>
        <Button
          onClick={() => {
            _setEditingClient(null);
            _setNewClientData(initialFormState);
            _setCreateError(null);
            _setIsFormVisible(true);
          }}
          variant='primary'
          size='md'
          className='w-full sm:w-auto'
        >
          <PlusCircleIcon className='h-5 w-5 mr-2' />
          <span className='hidden sm:inline font-medium'>新規取引先を追加</span>
          <span className='sm:hidden font-medium'>新規追加</span>
        </Button>
      </div>

      <FilterSection />

      {/* データが空の場合 */}
      {allPartners.length === 0 ? (
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center'>
          <div className='text-gray-500 dark:text-gray-400'>取引先がありません</div>
        </div>
      ) : (
        <div>
          {/* デスクトップ表示：テーブル */}
          <div className='hidden lg:block'>
            <PaginatedTable
              data={allPartners}
              loading={loading}
              storageKey='partnerList'
              onRowClick={partner => handleShowDetail(partner)}
              columns={[
                {
                  key: 'company',
                  label: '会社名',
                  render: partner => (
                    <div className='flex items-center space-x-2'>
                      {partner.dataSource === BusinessPartnerDataSource.CUSTOM ? (
                        <UserIcon
                          className='h-4 w-4 text-green-600 flex-shrink-0'
                          title='独自追加データ'
                        />
                      ) : (
                        <BuildingOffice2Icon
                          className='h-4 w-4 text-blue-500 flex-shrink-0'
                          title='ラクハイ提供データ'
                        />
                      )}
                      <div className='flex-1'>
                        <div className='flex items-center space-x-2'>
                          <span className='font-medium text-gray-900 dark:text-white text-sm'>
                            {partner.source === 'businessPartner' && partner.displayName
                              ? partner.displayName
                              : partner.companyName}
                          </span>
                          {partner.dataSource === BusinessPartnerDataSource.CUSTOM && (
                            <Badge variant='success' size='sm'>
                              <UserIcon className='h-3 w-3 inline mr-1' />
                              独自追加
                            </Badge>
                          )}
                        </div>
                        {partner.source === 'businessPartner' && partner.displayName && (
                          <div className='text-xs text-gray-500 dark:text-gray-400'>
                            {partner.companyName}
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'category',
                  label: 'カテゴリ',
                  render: partner => (
                    <span className='text-sm text-gray-700 dark:text-gray-300'>
                      {getCategoryLabel(partner.category)}
                    </span>
                  ),
                },
                {
                  key: 'availableRoles',
                  label: '対応可能役割',
                  render: partner => (
                    <div className='flex flex-wrap gap-1'>
                      {(partner.availableRoles ?? []).map(role => (
                        <span
                          key={role}
                          className='inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                        >
                          {getRoleLabel(role)}
                        </span>
                      ))}
                      {(!partner.availableRoles || partner.availableRoles.length === 0) && (
                        <span className='text-sm text-gray-500 dark:text-gray-400'>未設定</span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'deadlineRules',
                  label: '締切ルール',
                  render: partner =>
                    hasDeadlineRules(partner.id) ? (
                      <Badge variant='info' size='sm'>
                        <ClockIcon className='h-3 w-3 inline mr-1' />
                        締切ルールあり
                      </Badge>
                    ) : (
                      <span className='text-sm text-gray-500 dark:text-gray-400'>-</span>
                    ),
                },
              ]}
              rowKey={partner => `${partner.source}-${partner.id}`}
              emptyMessage='取引先がありません'
            />
          </div>

          {/* モバイル表示：カード */}
          <div className='lg:hidden'>
            <PaginatedCards
              data={allPartners}
              loading={loading}
              storageKey='partnerList'
              keyExtractor={partner => `${partner.source}-${partner.id}`}
              emptyMessage='取引先がありません'
              renderCard={partner => (
                <div
                  className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-5 flex flex-col justify-between card-hover card-touch touch-target fade-in cursor-pointer'
                  onClick={() => handleShowDetail(partner)}
                >
                  <div>
                    <div className='flex items-center space-x-2'>
                      {/* データソース表示アイコン */}
                      {partner.dataSource === BusinessPartnerDataSource.CUSTOM ? (
                        <UserIcon
                          className='h-4 w-4 text-green-600 flex-shrink-0'
                          title='独自追加データ'
                        />
                      ) : (
                        <BuildingOffice2Icon
                          className='h-4 w-4 text-blue-500 flex-shrink-0'
                          title='ラクハイ提供データ'
                        />
                      )}
                      <h3 className='text-base sm:text-lg font-bold text-gray-800 dark:text-white break-words'>
                        {partner.source === 'businessPartner' && partner.displayName
                          ? partner.displayName
                          : partner.companyName}
                      </h3>
                    </div>

                    {/* カテゴリのバッジ */}
                    <div className='mt-2 flex flex-wrap gap-2'>
                      <Badge
                        variant={
                          partner.category === BusinessPartnerCategory.DEALERSHIP
                            ? 'success'
                            : partner.category === BusinessPartnerCategory.AUCTION
                              ? 'warning'
                              : 'secondary'
                        }
                        size='sm'
                      >
                        <TagIcon className='h-3 w-3 inline mr-1' />
                        {getCategoryLabel(partner.category)}
                      </Badge>
                      {/* データソースバッジ（独自追加のみ表示） */}
                      {partner.dataSource === BusinessPartnerDataSource.CUSTOM && (
                        <Badge variant='info' size='sm'>
                          <UserIcon className='h-3 w-3 inline mr-1' />
                          独自追加
                        </Badge>
                      )}
                      {/* 締切ルールバッジ */}
                      {hasDeadlineRules(partner.id) && (
                        <Badge variant='info' size='sm'>
                          <ClockIcon className='h-3 w-3 inline mr-1' />
                          締切ルールあり
                        </Badge>
                      )}
                    </div>

                    <div className='mt-2 space-y-1'>
                      <p className='text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate'>
                        {partner.address}
                      </p>
                    </div>

                    {/* 対応可能役割 */}
                    <div className='mt-3'>
                      <div className='text-xs font-medium text-gray-500 dark:text-gray-400 mb-1'>
                        対応可能役割:
                      </div>
                      <div className='flex flex-wrap gap-1'>
                        {(partner.availableRoles ?? []).map(role => (
                          <span
                            key={role}
                            className='inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                          >
                            {getRoleLabel(role)}
                          </span>
                        ))}
                        {(!partner.availableRoles || partner.availableRoles.length === 0) && (
                          <span className='text-xs text-gray-500 dark:text-gray-400'>未設定</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='mt-4 sm:mt-5 flex justify-between items-center'>
                    {/* 詳細表示ボタン（全取引先共通） */}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleShowDetail(partner);
                      }}
                      className='text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-200 p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center touch-target button-touch focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                      title='詳細表示'
                      aria-label='取引先詳細を表示'
                    >
                      <EyeIcon className='h-4 w-4' />
                    </button>

                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                      クリックして詳細を表示
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* 詳細モーダル */}
      {_selectedBusinessPartner && (
        <BusinessPartnerDetailModal
          isOpen={_detailModalOpen}
          onClose={_handleCloseDetailModal}
          partner={_selectedBusinessPartner}
          onEdit={partner => {
            logger.info('取引先編集開始:', partner);
            _setEditingPartner(partner);
            _setIsEditFormVisible(true);
            _handleCloseDetailModal();
          }}
          onUpdate={async updatedPartner => {
            try {
              logger.info('取引先データ更新:', updatedPartner);
              await updateBusinessPartner(updatedPartner.id, updatedPartner);
              _setSelectedBusinessPartner(updatedPartner);
              await refreshData(); // データを再読み込み
            } catch (error) {
              logger.error('取引先更新エラー:', error);
            }
          }}
        />
      )}

      {/* 新規作成モーダル */}
      <BusinessPartnerFormModal
        isOpen={_isFormVisible}
        onClose={() => {
          _setIsFormVisible(false);
          _setCreateError(null);
        }}
        onSave={async data => {
          try {
            _setCreateError(null);
            logger.info('新規取引先作成開始:', data);

            // companyIdを追加
            const createData = {
              ...data,
              companyId: user?.companyId || 'e32a0499-6456-44ac-9b16-b0001098c259',
              dataSource: BusinessPartnerDataSource.CUSTOM,
            };

            await addBusinessPartner(createData as BusinessPartner);
            logger.info('新規取引先作成完了');
            _setIsFormVisible(false);
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : '取引先の作成に失敗しました';
            logger.error('新規取引先作成エラー:', error);
            _setCreateError(errorMessage);
            throw new Error(errorMessage);
          }
        }}
        partner={null}
        mode='create'
      />

      {/* 編集用モーダル */}
      {_isEditFormVisible && _editingPartner && (
        <BusinessPartnerFormModal
          isOpen={_isEditFormVisible}
          onClose={() => {
            _setIsEditFormVisible(false);
            _setEditingPartner(null);
            _setCreateError(null);
          }}
          onSave={async data => {
            try {
              _setCreateError(null);
              logger.info('取引先更新開始:', data);

              const updateData = {
                ...data,
                id: _editingPartner.id,
                companyId: _editingPartner.companyId,
                dataSource: _editingPartner.dataSource,
              };

              await updateBusinessPartner(_editingPartner.id, updateData);
              logger.info('取引先更新完了');
              _setIsEditFormVisible(false);
              _setEditingPartner(null);
              await refreshData(); // データを再読み込み
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : '取引先の更新に失敗しました';
              logger.error('取引先更新エラー:', error);
              _setCreateError(errorMessage);
              throw new Error(errorMessage);
            }
          }}
          partner={_editingPartner}
          mode='edit'
        />
      )}
    </div>
  );

  return <_PartnerList />;
};

export default PartnerListPage;
